import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { MessageSquare, AlertCircle } from 'lucide-react';

interface Ticket {
  id: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_name?: string;
  messages?: Array<{
    id: string;
    message: string;
    created_at: string;
    user_email: string;
    is_internal: boolean;
  }>;
}

export function TicketsManagement() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      const loadMessages = async () => {
        const messages = await fetchTicketMessages(selectedTicket.id);
        setSelectedTicket(prev => prev ? { ...prev, messages } : null);
      };

      loadMessages();
      const interval = setInterval(loadMessages, 5000);

      return () => clearInterval(interval);
    }
  }, [selectedTicket?.id]);

  async function fetchTickets() {
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const { data: allUsers } = await supabase.rpc('get_all_users_for_admin');
      const usersMap = new Map((allUsers || []).map((u: any) => [u.id, u]));

      const ticketsWithUsers = (data || []).map((ticket) => {
        const userData = usersMap.get(ticket.user_id) as any;
        return {
          ...ticket,
          user_email: userData?.email || 'Unknown',
          user_name: userData?.first_name && userData?.last_name
            ? `${userData.first_name} ${userData.last_name}`
            : userData?.email || 'Unknown',
        };
      });

      setTickets(ticketsWithUsers);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchTicketMessages(ticketId: string) {
    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const { data: allUsers } = await supabase.rpc('get_all_users_for_admin');
      const usersMap = new Map((allUsers || []).map((u: any) => [u.id, u]));

      const messagesWithUsers = (data || []).map((message) => {
        const userData = usersMap.get(message.user_id) as any;
        return {
          ...message,
          user_email: userData?.email || 'Unknown',
        };
      });

      return messagesWithUsers;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  }

  async function updateTicketStatus(ticketId: string, status: string) {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ status })
        .eq('id', ticketId);

      if (error) throw error;
      await fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status });
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  }

  async function sendMessage() {
    if (!selectedTicket || !newMessage.trim()) return;

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Error getting user:', userError);
        alert('Erreur lors de la récupération de l\'utilisateur');
        return;
      }
      if (!userData.user) {
        alert('Vous devez être connecté pour envoyer un message');
        return;
      }

      const { error } = await supabase.from('ticket_messages').insert({
        ticket_id: selectedTicket.id,
        user_id: userData.user.id,
        message: newMessage,
        is_internal: isInternalNote,
      });

      if (error) {
        console.error('Error inserting message:', error);
        alert(`Erreur lors de l'envoi du message: ${error.message}`);
        throw error;
      }

      const messages = await fetchTicketMessages(selectedTicket.id);
      setSelectedTicket({ ...selectedTicket, messages });
      setNewMessage('');
      setIsInternalNote(false);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }

  async function openTicketDetails(ticket: Ticket) {
    const messages = await fetchTicketMessages(ticket.id);
    setSelectedTicket({ ...ticket, messages });
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Chargement des tickets...</div>
      </div>
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Tickets de support</h2>
        <div className="text-sm text-gray-600">{tickets.length} ticket(s)</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sujet</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priorité</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tickets.map((ticket) => (
              <tr key={ticket.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={16} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{ticket.subject}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-gray-900">{ticket.user_name}</div>
                  <div className="text-xs text-gray-500">{ticket.user_email}</div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority === 'urgent' && <AlertCircle size={12} />}
                    {ticket.priority}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-4 py-4">
                  <button
                    onClick={() => openTicketDetails(ticket)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Voir détails
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedTicket.subject}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Par {selectedTicket.user_name} - {new Date(selectedTicket.created_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{selectedTicket.description}</p>
            </div>

            <div className="mb-4 flex gap-2">
              <button
                onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}
                className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                En cours
              </button>
              <button
                onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                Résolu
              </button>
              <button
                onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Fermé
              </button>
            </div>

            <div className="border-t border-gray-200 pt-4 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">Messages</h4>
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg ${
                      msg.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-900">{msg.user_email}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(msg.created_at).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{msg.message}</p>
                    {msg.is_internal && (
                      <span className="text-xs text-yellow-700 font-medium">Note interne</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <div className={`border-2 rounded-lg p-1 ${isInternalNote ? 'border-yellow-400 bg-yellow-50' : 'border-green-400 bg-green-50'}`}>
                  <div className="flex items-center justify-between px-2 py-1 mb-2">
                    <span className={`text-xs font-semibold ${isInternalNote ? 'text-yellow-800' : 'text-green-800'}`}>
                      {isInternalNote ? '📝 NOTE INTERNE (invisible pour le client)' : '✉️ MESSAGE PUBLIC (visible pour le client)'}
                    </span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs font-medium text-gray-700">Note interne</span>
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                      />
                    </label>
                  </div>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={isInternalNote ? "Rédigez une note interne (invisible pour le client)..." : "Rédigez votre réponse au client..."}
                    className="w-full px-3 py-2 border-0 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none bg-white"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={sendMessage}
                    className={`px-6 py-2 text-white rounded-lg font-medium ${
                      isInternalNote
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isInternalNote ? '📝 Enregistrer la note' : '✉️ Envoyer au client'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
